import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface FacturaIesire {
  id: string;
  numar: string;
  data: string;
  client: string;
  descriere: string;
  suma: number;
  tva: number;
  total: number;
  parohieId?: string;
  status: 'emisa' | 'platita' | 'anulata';
  dataScadenta: string;
  observatii?: string;
}

const FacturiIesire: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState<FacturaIesire | null>(null);

  // Mock data pentru facturi ieșire
  const [facturiIesire] = useState<FacturaIesire[]>([
    {
      id: 'fe1',
      numar: 'FE-001/2024',
      data: '2024-01-10',
      client: 'Credinciosul Ion Popescu',
      descriere: 'Servicii religioase - botez',
      suma: 200,
      tva: 38,
      total: 238,
      parohieId: 'parohia1',
      status: 'emisa',
      dataScadenta: '2024-02-10'
    },
    {
      id: 'fe2',
      numar: 'FE-002/2024',
      data: '2024-01-12',
      client: 'Credincioasa Maria Ionescu',
      descriere: 'Servicii religioase - cununie',
      suma: 300,
      tva: 57,
      total: 357,
      parohieId: 'parohia1',
      status: 'platita',
      dataScadenta: '2024-02-12'
    },
    {
      id: 'fe3',
      numar: 'FE-003/2024',
      data: '2024-01-15',
      client: 'Familia Popescu',
      descriere: 'Servicii religioase - înmormântare',
      suma: 500,
      tva: 95,
      total: 595,
      parohieId: 'parohia1',
      status: 'emisa',
      dataScadenta: '2024-02-15'
    }
  ]);

  const clienti = Array.from(new Set(facturiIesire.map(f => f.client)));

  const filteredFacturi = facturiIesire.filter(factura => {
    const matchesSearch = factura.numar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         factura.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         factura.descriere.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || factura.status === filterStatus;
    const matchesClient = !filterClient || factura.client === filterClient;
    
    return matchesSearch && matchesStatus && matchesClient;
  });

  const totalEmise = facturiIesire
    .filter(f => f.status === 'emisa')
    .reduce((sum, f) => sum + f.total, 0);

  const totalPlatite = facturiIesire
    .filter(f => f.status === 'platita')
    .reduce((sum, f) => sum + f.total, 0);

  const totalGeneral = facturiIesire.reduce((sum, f) => sum + f.total, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'platita': return 'success';
      case 'emisa': return 'info';
      case 'anulata': return 'error';
      default: return 'default';
    }
  };

  const handleViewFactura = (factura: FacturaIesire) => {
    setSelectedFactura(factura);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFactura(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Facturi Ieșire
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare factură nouă */}}
          >
            Factură nouă
          </Button>
        )}
      </Box>

      {/* Statistici facturi */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  📄
                </Typography>
                <Typography color="textSecondary">
                  Facturi emise
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                €{totalEmise.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ✅
                </Typography>
                <Typography color="textSecondary">
                  Facturi plătite
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalPlatite.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Total general
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                €{totalGeneral.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru facturi */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută facturi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="emisa">Emisă</MenuItem>
                <MenuItem value="platita">Plătită</MenuItem>
                <MenuItem value="anulata">Anulată</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                value={filterClient}
                onChange={(e) => setFilterClient(e.target.value)}
                label="Client"
              >
                <MenuItem value="">Toți</MenuItem>
                {clienti.map(client => (
                  <MenuItem key={client} value={client}>{client}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
                setFilterClient('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel facturi */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Număr</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Descriere</TableCell>
              <TableCell>Suma</TableCell>
              <TableCell>TVA</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data scadență</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFacturi.map((factura) => (
              <TableRow key={factura.id}>
                <TableCell>{factura.numar}</TableCell>
                <TableCell>{factura.data}</TableCell>
                <TableCell>{factura.client}</TableCell>
                <TableCell>{factura.descriere}</TableCell>
                <TableCell>€{factura.suma.toLocaleString()}</TableCell>
                <TableCell>€{factura.tva.toLocaleString()}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    €{factura.total.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={factura.status}
                    color={getStatusColor(factura.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{factura.dataScadenta}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewFactura(factura)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare factură */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii factură ieșire
        </DialogTitle>
        <DialogContent>
          {selectedFactura && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Număr factură
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.numar}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Client
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.client}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedFactura.status}
                  color={getStatusColor(selectedFactura.status) as any}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Descriere
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.descriere}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suma fără TVA
                </Typography>
                <Typography variant="h6">
                  €{selectedFactura.suma.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  TVA
                </Typography>
                <Typography variant="h6">
                  €{selectedFactura.tva.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedFactura.total.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data scadență
                </Typography>
                <Typography variant="body1">
                  {selectedFactura.dataScadenta}
                </Typography>
              </Grid>
              {selectedFactura.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedFactura.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacturiIesire;
